import * as d3array from 'd3-array';
import { DataTable, ValueAccessor } from './models';
import { DefaultMeasureAccessor } from './models/Accessor';
import { isNullOrUndefined } from './models/DataTable';

export const measureDomainToRender = (
  data: DataTable,
  maxPrecision: number = 0.01,
  accessors: ReadonlyArray<ValueAccessor<number | undefined> | undefined> = [DefaultMeasureAccessor]
): readonly [number, number] => {
  const values = collectValues(data, accessors);

  return adjustMeasureDomain(d3array.extent(values), maxPrecision);
};

const MIN_PRECISION_FACTOR = 5;

/**
 * This function creates an adjusted domain for the measure axis.
 *
 * It follows a few heuristics:
 * - Don't cross the 0 line unless the data does
 * - Leave some space above/below the value furthest from zero (currently 10%)
 * - Aim for "the golden ratio" for space between the value closest to zero and the edge of the chart
 * - Ensure the domain is larger than the maximum precision the metric will display
 *
 * @param measureDomain the domain of the data
 * @param maxPrecision the maximum precision that will be rendered (defaults to 0.01)
 */
export const adjustMeasureDomain = (
  measureDomain: readonly [number, number] | readonly [undefined, undefined],
  maxPrecision: number = 0.01
): readonly [number, number] => {
  if (measureDomain[0] === undefined) {
    return [0, 1];
  }
  const [dataMin, dataMax] = measureDomain;
  const measureDiff = Math.abs(dataMax - dataMin);

  const margin = measureDiff * 0.1;

  if (dataMin === dataMax) {
    if (dataMax === 0) {
      return [0, 1];
    } else if (dataMax > 0) {
      return [0, dataMax * 1.3];
    } else {
      return [dataMax * 1.3, 0];
    }
  }

  if (measureDiff < maxPrecision * MIN_PRECISION_FACTOR) {
    return [dataMin - maxPrecision * 2, dataMax + maxPrecision * 2];
  }

  if (dataMin < 0 && dataMax > 0) {
    return [dataMin - margin, dataMax + margin];
  }

  if (dataMin === 0) {
    return [dataMin, dataMax + margin];
  }

  if (dataMax === 0) {
    return [dataMin - margin, dataMax];
  }

  if (dataMin > 0) {
    const scaleMax = dataMax + margin;
    return [goldenRatioClampedToZero(dataMin, scaleMax), scaleMax];
  } else {
    const scaleFurthestFromZero = dataMin - margin;
    return [scaleFurthestFromZero, goldenRatioClampedToZero(dataMax, scaleFurthestFromZero)];
  }
};

const goldenRatioClampedToZero = (closestToZero: number, scaleFurthestFromZero: number): number => {
  if (closestToZero === 0) {
    return 0;
  }
  // See https://www.chezvoila.com/blog/yaxis
  const idealValue = (3 * closestToZero - scaleFurthestFromZero) / 2;

  if ((closestToZero > 0 && idealValue < 0) || (closestToZero < 0 && idealValue > 0)) {
    // clamp to zero if the ideal value would cross the zero line
    return 0;
  }

  return idealValue;
};

export const timeDomainToRender = (data: DataTable): readonly [Date, Date] | readonly [undefined, undefined] => {
  return d3array.extent(data, (p) => p.timestamp as Date);
};

export const collectValues = (
  data: DataTable,
  accessors: ReadonlyArray<ValueAccessor<number | undefined> | undefined>
): number[] => {
  const values: number[] = [];
  data.forEach((d) =>
    accessors.forEach((a) => {
      const value = a?.(d);
      if (!isNullOrUndefined(value)) {
        values.push(value);
      }
    })
  );
  return values;
};
